import { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useToast } from '../hooks/use-toast';
import {
  getFiscalCreditsSummary, getFiscalCreditPurchases, getFiscalCreditPurchase,
  createFiscalCreditPurchase, updateFiscalCreditPurchase, deleteFiscalCreditPurchase,
  getFiscalCreditUsages, createFiscalCreditUsage, updateFiscalCreditUsage,
  deleteFiscalCreditUsage, getFiscalCreditMovements,
} from '../lib/api';
import { format, parseISO } from 'date-fns';
import { Loader2, Plus, Pencil, Trash2, Eye, X, Coins, TrendingDown, TrendingUp, Wallet, PiggyBank } from 'lucide-react';

// ─── Formattatori italiani ─────────────────────────────────────────
const fmtEUR = (v) => `€ ${Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtEURcompact = (v) => `€ ${Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtPct = (v) => `${Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
const fmtDate = (isoStr) => {
  if (!isoStr) return '—';
  try { return format(parseISO(isoStr.length === 10 ? isoStr : isoStr.split('T')[0]), 'dd/MM/yyyy'); }
  catch { return isoStr; }
};

// Anni selezionabili per usable_from_year (range statico ragionevole)
const YEARS = Array.from({ length: 21 }, (_, i) => new Date().getFullYear() - 5 + i);

export default function FiscalCreditsPage() {
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('purchases');
  const [summary, setSummary] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [usages, setUsages] = useState([]);
  const [movements, setMovements] = useState([]);

  // Purchase dialog
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [purchaseForm, setPurchaseForm] = useState({
    nominal_amount: '', purchase_price: '', purchase_date: '',
    usable_from_year: String(new Date().getFullYear()), reference: '', notes: '',
  });

  // Purchase detail dialog (nel nuovo modello anno-based non mostriamo utilizzi collegati)
  const [detailPurchase, setDetailPurchase] = useState(null);

  // Usage dialog
  const [usageDialogOpen, setUsageDialogOpen] = useState(false);
  const [editingUsage, setEditingUsage] = useState(null);
  const [usageForm, setUsageForm] = useState({
    usable_from_year: '', amount: '', usage_date: '', reference: '', notes: '',
  });

  // Delete confirmations
  const [purchaseToDelete, setPurchaseToDelete] = useState(null);
  const [usageToDelete, setUsageToDelete] = useState(null);

  // Filters — Purchases (nel modello anno-based non c'è più residuo per-singolo → niente filtro "Stato residuo")
  const [pFilterYear, setPFilterYear] = useState('all');
  const [pFilterFrom, setPFilterFrom] = useState('');
  const [pFilterTo, setPFilterTo] = useState('');
  const [pFilterRef, setPFilterRef] = useState('');

  // Filters — Usages (nuovo modello: filtro per anno di competenza, non più per acquisto)
  const [uFilterYear, setUFilterYear] = useState('all');
  const [uFilterFrom, setUFilterFrom] = useState('');
  const [uFilterTo, setUFilterTo] = useState('');
  const [uFilterCompetenceYear, setUFilterCompetenceYear] = useState('all');
  const [uFilterRef, setUFilterRef] = useState('');

  // Filters — Movements
  const [mFilterType, setMFilterType] = useState('all');
  const [mFilterYear, setMFilterYear] = useState('all');
  const [mFilterFrom, setMFilterFrom] = useState('');
  const [mFilterTo, setMFilterTo] = useState('');

  const load = useCallback(async (isInitial = false) => {
    if (isInitial) setLoading(true);
    try {
      const [sumRes, pRes, uRes, mRes] = await Promise.all([
        getFiscalCreditsSummary(), getFiscalCreditPurchases(),
        getFiscalCreditUsages(), getFiscalCreditMovements(),
      ]);
      setSummary(sumRes.data);
      setPurchases(pRes.data.data || []);
      setUsages(uRes.data.data || []);
      setMovements(mRes.data.data || []);
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Caricamento fallito', variant: 'destructive' });
    } finally { if (isInitial) setLoading(false); }
  }, [toast]);

  useEffect(() => { load(true); }, [load]);

  // ─── Purchase actions ────────────────────────────────────────────
  const openNewPurchase = () => {
    setEditingPurchase(null);
    setPurchaseForm({
      nominal_amount: '', purchase_price: '', purchase_date: format(new Date(), 'yyyy-MM-dd'),
      usable_from_year: String(new Date().getFullYear()), reference: '', notes: '',
    });
    setPurchaseDialogOpen(true);
  };

  const openEditPurchase = (p) => {
    setEditingPurchase(p);
    setPurchaseForm({
      nominal_amount: String(p.nominal_amount ?? ''),
      purchase_price: String(p.purchase_price ?? ''),
      purchase_date: p.purchase_date?.length === 10 ? p.purchase_date : (p.purchase_date || '').split('T')[0],
      usable_from_year: String(p.usable_from_year ?? new Date().getFullYear()),
      reference: p.reference || '', notes: p.notes || '',
    });
    setPurchaseDialogOpen(true);
  };

  const savePurchase = async (e) => {
    e.preventDefault();
    const nominal = parseFloat(purchaseForm.nominal_amount);
    const price = parseFloat(purchaseForm.purchase_price);
    if (!Number.isFinite(nominal) || nominal <= 0) {
      toast({ title: 'Errore', description: 'Importo nominale deve essere > 0', variant: 'destructive' });
      return;
    }
    if (!Number.isFinite(price) || price <= 0) {
      toast({ title: 'Errore', description: 'Prezzo di acquisto deve essere > 0', variant: 'destructive' });
      return;
    }
    if (!purchaseForm.purchase_date) {
      toast({ title: 'Errore', description: 'Data di acquisto obbligatoria', variant: 'destructive' });
      return;
    }
    const payload = {
      nominal_amount: nominal,
      purchase_price: price,
      purchase_date: purchaseForm.purchase_date,
      usable_from_year: parseInt(purchaseForm.usable_from_year, 10),
      reference: purchaseForm.reference?.trim() || null,
      notes: purchaseForm.notes?.trim() || null,
    };
    try {
      if (editingPurchase) {
        await updateFiscalCreditPurchase(editingPurchase.id, payload);
        toast({ title: 'Acquisto aggiornato' });
      } else {
        await createFiscalCreditPurchase(payload);
        toast({ title: 'Acquisto registrato' });
      }
      setPurchaseDialogOpen(false);
      await load();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Salvataggio fallito', variant: 'destructive' });
    }
  };

  const confirmDeletePurchase = async () => {
    if (!purchaseToDelete) return;
    try {
      await deleteFiscalCreditPurchase(purchaseToDelete.id);
      toast({ title: 'Acquisto eliminato' });
      setPurchaseToDelete(null);
      await load();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Eliminazione fallita', variant: 'destructive' });
      setPurchaseToDelete(null);
    }
  };

  const viewPurchase = async (p) => {
    try {
      const res = await getFiscalCreditPurchase(p.id);
      setDetailPurchase(res.data.purchase);
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile caricare il dettaglio', variant: 'destructive' });
    }
  };

  // ─── Usage actions ───────────────────────────────────────────────
  const openNewUsage = (presetYear = null) => {
    setEditingUsage(null);
    setUsageForm({
      usable_from_year: presetYear ? String(presetYear) : '',
      amount: '', usage_date: format(new Date(), 'yyyy-MM-dd'), reference: '', notes: '',
    });
    setUsageDialogOpen(true);
  };

  const openEditUsage = (u) => {
    setEditingUsage(u);
    setUsageForm({
      usable_from_year: String(u.usable_from_year ?? ''),
      amount: String(u.amount ?? ''),
      usage_date: u.usage_date?.length === 10 ? u.usage_date : (u.usage_date || '').split('T')[0],
      reference: u.reference || '', notes: u.notes || '',
    });
    setUsageDialogOpen(true);
  };

  const saveUsage = async (e) => {
    e.preventDefault();
    const amount = parseFloat(usageForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast({ title: 'Errore', description: 'Importo deve essere > 0', variant: 'destructive' });
      return;
    }
    if (!usageForm.usable_from_year) {
      toast({ title: 'Errore', description: 'Seleziona un anno di competenza', variant: 'destructive' });
      return;
    }
    if (!usageForm.usage_date) {
      toast({ title: 'Errore', description: 'Data di utilizzo obbligatoria', variant: 'destructive' });
      return;
    }
    const payload = {
      usable_from_year: parseInt(usageForm.usable_from_year, 10),
      amount,
      usage_date: usageForm.usage_date,
      reference: usageForm.reference?.trim() || null,
      notes: usageForm.notes?.trim() || null,
    };
    try {
      if (editingUsage) {
        await updateFiscalCreditUsage(editingUsage.id, payload);
        toast({ title: 'Utilizzo aggiornato' });
      } else {
        await createFiscalCreditUsage(payload);
        toast({ title: 'Utilizzo registrato' });
      }
      setUsageDialogOpen(false);
      await load();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Salvataggio fallito', variant: 'destructive' });
    }
  };

  const confirmDeleteUsage = async () => {
    if (!usageToDelete) return;
    try {
      await deleteFiscalCreditUsage(usageToDelete.id);
      toast({ title: 'Utilizzo eliminato' });
      setUsageToDelete(null);
      await load();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Eliminazione fallita', variant: 'destructive' });
      setUsageToDelete(null);
    }
  };

  // ─── Derived: Purchases filtered ─────────────────────────────────
  const filteredPurchases = useMemo(() => {
    return purchases.filter(p => {
      if (pFilterYear !== 'all' && String(p.usable_from_year) !== pFilterYear) return false;
      if (pFilterFrom && (p.purchase_date || '') < pFilterFrom) return false;
      if (pFilterTo && (p.purchase_date || '') > pFilterTo) return false;
      if (pFilterRef && !(p.reference || '').toLowerCase().includes(pFilterRef.toLowerCase())) return false;
      return true;
    });
  }, [purchases, pFilterYear, pFilterFrom, pFilterTo, pFilterRef]);

  const filteredUsages = useMemo(() => {
    return usages.filter(u => {
      const yr = String((u.usage_date || '').slice(0, 4));
      if (uFilterYear !== 'all' && yr !== uFilterYear) return false;
      if (uFilterFrom && (u.usage_date || '') < uFilterFrom) return false;
      if (uFilterTo && (u.usage_date || '') > uFilterTo) return false;
      if (uFilterCompetenceYear !== 'all' && String(u.usable_from_year) !== uFilterCompetenceYear) return false;
      if (uFilterRef && !(u.reference || '').toLowerCase().includes(uFilterRef.toLowerCase())) return false;
      return true;
    });
  }, [usages, uFilterYear, uFilterFrom, uFilterTo, uFilterCompetenceYear, uFilterRef]);

  const filteredMovements = useMemo(() => {
    return movements.filter(m => {
      if (mFilterType !== 'all' && m.type !== mFilterType) return false;
      const yr = String((m.date || '').slice(0, 4));
      if (mFilterYear !== 'all' && yr !== mFilterYear) return false;
      if (mFilterFrom && (m.date || '') < mFilterFrom) return false;
      if (mFilterTo && (m.date || '') > mFilterTo) return false;
      return true;
    });
  }, [movements, mFilterType, mFilterYear, mFilterFrom, mFilterTo]);

  // Set unico di anni presenti (per i filtri "Anno")
  const purchaseYears = useMemo(() => [...new Set(purchases.map(p => String(p.usable_from_year)))].sort(), [purchases]);
  const usageYears = useMemo(() => [...new Set(usages.map(u => String((u.usage_date || '').slice(0, 4))).filter(Boolean))].sort(), [usages]);
  const movementYears = useMemo(() => [...new Set(movements.map(m => String((m.date || '').slice(0, 4))).filter(Boolean))].sort(), [movements]);

  const resetPurchaseFilters = () => {
    setPFilterYear('all'); setPFilterFrom(''); setPFilterTo(''); setPFilterRef('');
  };
  const resetUsageFilters = () => {
    setUFilterYear('all'); setUFilterFrom(''); setUFilterTo(''); setUFilterCompetenceYear('all'); setUFilterRef('');
  };
  const resetMovementFilters = () => {
    setMFilterType('all'); setMFilterYear('all'); setMFilterFrom(''); setMFilterTo('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  // Anni disponibili per il form utilizzi (solo anni con residuo > 0, oppure quello attualmente in edit)
  const availableUsageYears = (summary?.by_year || [])
    .filter(y => y.residual_amount > 0.005 || (editingUsage && String(editingUsage.usable_from_year) === String(y.year)))
    .sort((a, b) => a.year - b.year);
  const selectedYearInfo = summary?.by_year?.find(y => String(y.year) === String(usageForm.usable_from_year));
  // Residuo disponibile per il form (aggiungendo indietro l'importo se stiamo modificando lo stesso anno)
  const residualForForm = selectedYearInfo
    ? (editingUsage && String(editingUsage.usable_from_year) === String(selectedYearInfo.year)
        ? selectedYearInfo.residual_amount + Number(editingUsage.amount || 0)
        : selectedYearInfo.residual_amount)
    : 0;

  // Calcoli in tempo reale per il form acquisto (sconto e %)
  const formNominal = parseFloat(purchaseForm.nominal_amount) || 0;
  const formPrice = parseFloat(purchaseForm.purchase_price) || 0;
  const formDiscount = formNominal - formPrice;
  const formPct = formNominal > 0 ? (formPrice / formNominal * 100) : 0;

  return (
    <div className="space-y-6" data-testid="fiscal-credits-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Coins className="w-7 h-7 text-orange-600" />
            Crediti Fiscali
          </h1>
          <p className="text-sm text-gray-500 mt-1">Gestisci acquisti e utilizzi dei crediti fiscali aziendali.</p>
        </div>
      </div>

      {/* Riepilogo cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard icon={PiggyBank} label="Credito nominale acquistato" value={fmtEUR(summary?.total_nominal)} sub={`${summary?.purchases_count || 0} acquisti`} color="blue" testid="card-total-nominal" />
        <SummaryCard icon={TrendingDown} label="Totale pagato" value={fmtEUR(summary?.total_paid)} sub={`Sconto: ${fmtEUR(summary?.total_discount)}`} color="amber" testid="card-total-paid" />
        <SummaryCard icon={TrendingUp} label="Credito speso" value={fmtEUR(summary?.total_used)} sub={`${summary?.usages_count || 0} utilizzi`} color="red" testid="card-total-used" />
        <SummaryCard icon={Wallet} label="Credito residuo" value={fmtEUR(summary?.residual_total)} sub="Disponibile totale" color="green" testid="card-total-residual" />
      </div>

      {/* Residuo per anno */}
      {summary?.by_year?.length > 0 && (
        <Card data-testid="by-year-card">
          <CardHeader className="pb-3">
            <div className="font-semibold text-gray-900">Credito residuo per anno di spendibilità</div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {summary.by_year.map(y => (
                <div key={y.year} className="border rounded-lg p-3 bg-gray-50" data-testid={`year-${y.year}`}>
                  <div className="text-xs text-gray-500">Disponibile dal {y.year}</div>
                  <div className={`text-lg font-bold ${y.residual_amount <= 0 ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                    {fmtEURcompact(y.residual_amount)}
                  </div>
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    Nom. {fmtEURcompact(y.nominal_amount)} · Speso {fmtEURcompact(y.used_amount)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList data-testid="fiscal-credits-tabs">
          <TabsTrigger value="purchases" data-testid="tab-purchases">Acquisti</TabsTrigger>
          <TabsTrigger value="usages" data-testid="tab-usages">Crediti spesi</TabsTrigger>
          <TabsTrigger value="movements" data-testid="tab-movements">Movimenti</TabsTrigger>
        </TabsList>

        {/* ─── Tab Acquisti ─────────────────────────── */}
        <TabsContent value="purchases" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <div className="text-sm font-semibold text-gray-700">Elenco acquisti</div>
              <Button onClick={openNewPurchase} data-testid="btn-new-purchase" className="bg-orange-600 hover:bg-orange-700">
                <Plus className="w-4 h-4 mr-1" /> Nuovo acquisto
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filtri */}
              <div className="flex flex-wrap gap-2 items-end">
                <div className="min-w-[140px]">
                  <Label className="text-xs">Anno spendibilità</Label>
                  <Select value={pFilterYear} onValueChange={setPFilterYear}>
                    <SelectTrigger data-testid="filter-p-year"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti</SelectItem>
                      {purchaseYears.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Dal</Label>
                  <Input type="date" value={pFilterFrom} onChange={(e) => setPFilterFrom(e.target.value)} className="w-[150px]" data-testid="filter-p-from" />
                </div>
                <div>
                  <Label className="text-xs">Al</Label>
                  <Input type="date" value={pFilterTo} onChange={(e) => setPFilterTo(e.target.value)} className="w-[150px]" data-testid="filter-p-to" />
                </div>
                <div>
                  <Label className="text-xs">Riferimento</Label>
                  <Input type="text" placeholder="cerca..." value={pFilterRef} onChange={(e) => setPFilterRef(e.target.value)} className="w-[180px]" data-testid="filter-p-ref" />
                </div>
                <Button variant="ghost" size="sm" onClick={resetPurchaseFilters} data-testid="btn-reset-p-filters">
                  <X className="w-4 h-4 mr-1" /> Azzera filtri
                </Button>
              </div>

              {/* Tabella */}
              {filteredPurchases.length === 0 ? (
                <EmptyState label={purchases.length === 0 ? "Non hai ancora registrato acquisti di crediti fiscali." : "Nessun acquisto corrisponde ai filtri."} />
              ) : (
                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data acquisto</TableHead>
                        <TableHead>Riferimento</TableHead>
                        <TableHead className="text-right">Nominale</TableHead>
                        <TableHead className="text-right">Prezzo</TableHead>
                        <TableHead className="text-right">% acq.</TableHead>
                        <TableHead>Anno competenza</TableHead>
                        <TableHead className="text-right">Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredPurchases.map(p => (
                        <TableRow key={p.id} data-testid={`purchase-row-${p.id}`}>
                          <TableCell>{fmtDate(p.purchase_date)}</TableCell>
                          <TableCell className="text-sm text-gray-700">{p.reference || '—'}</TableCell>
                          <TableCell className="text-right tabular-nums">{fmtEUR(p.nominal_amount)}</TableCell>
                          <TableCell className="text-right tabular-nums text-gray-600">{fmtEUR(p.purchase_price)}</TableCell>
                          <TableCell className="text-right tabular-nums">{fmtPct(p.purchase_percentage)}</TableCell>
                          <TableCell><Badge variant="secondary">{p.usable_from_year}</Badge></TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => viewPurchase(p)} title="Visualizza dettaglio" data-testid={`view-purchase-${p.id}`}>
                                <Eye className="w-4 h-4 text-gray-500" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => openEditPurchase(p)} title="Modifica" data-testid={`edit-purchase-${p.id}`}>
                                <Pencil className="w-4 h-4 text-gray-500" />
                              </Button>
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => setPurchaseToDelete(p)}
                                title="Elimina"
                                data-testid={`delete-purchase-${p.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Tab Utilizzi ────────────────────────── */}
        <TabsContent value="usages" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <div className="text-sm font-semibold text-gray-700">Crediti spesi</div>
              <Button
                onClick={() => openNewUsage(null)}
                disabled={(summary?.by_year || []).every(y => y.residual_amount <= 0.005)}
                data-testid="btn-new-usage"
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Plus className="w-4 h-4 mr-1" /> Registra F24
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filtri */}
              <div className="flex flex-wrap gap-2 items-end">
                <div className="min-w-[120px]">
                  <Label className="text-xs">Anno</Label>
                  <Select value={uFilterYear} onValueChange={setUFilterYear}>
                    <SelectTrigger data-testid="filter-u-year"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti</SelectItem>
                      {usageYears.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Dal</Label>
                  <Input type="date" value={uFilterFrom} onChange={(e) => setUFilterFrom(e.target.value)} className="w-[150px]" data-testid="filter-u-from" />
                </div>
                <div>
                  <Label className="text-xs">Al</Label>
                  <Input type="date" value={uFilterTo} onChange={(e) => setUFilterTo(e.target.value)} className="w-[150px]" data-testid="filter-u-to" />
                </div>
                <div className="min-w-[200px]">
                  <Label className="text-xs">Anno di competenza</Label>
                  <Select value={uFilterCompetenceYear} onValueChange={setUFilterCompetenceYear}>
                    <SelectTrigger data-testid="filter-u-competence-year"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti</SelectItem>
                      {[...new Set(usages.map(u => String(u.usable_from_year || '')).filter(Boolean))].sort().map(y => (
                        <SelectItem key={y} value={y}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Riferimento</Label>
                  <Input type="text" placeholder="cerca..." value={uFilterRef} onChange={(e) => setUFilterRef(e.target.value)} className="w-[180px]" data-testid="filter-u-ref" />
                </div>
                <Button variant="ghost" size="sm" onClick={resetUsageFilters} data-testid="btn-reset-u-filters">
                  <X className="w-4 h-4 mr-1" /> Azzera filtri
                </Button>
              </div>

              {/* Tabella */}
              {filteredUsages.length === 0 ? (
                <EmptyState label={usages.length === 0 ? "Non hai ancora registrato crediti spesi." : "Nessun utilizzo corrisponde ai filtri."} />
              ) : (
                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data utilizzo</TableHead>
                        <TableHead>Riferimento</TableHead>
                        <TableHead>Anno di competenza</TableHead>
                        <TableHead className="text-right">Importo</TableHead>
                        <TableHead>Note</TableHead>
                        <TableHead className="text-right">Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsages.map(u => (
                        <TableRow key={u.id} data-testid={`usage-row-${u.id}`}>
                          <TableCell>{fmtDate(u.usage_date)}</TableCell>
                          <TableCell className="text-sm text-gray-700">{u.reference || '—'}</TableCell>
                          <TableCell><Badge variant="secondary">{u.usable_from_year}</Badge></TableCell>
                          <TableCell className="text-right tabular-nums text-red-600 font-semibold">{fmtEUR(u.amount)}</TableCell>
                          <TableCell className="text-sm text-gray-500 max-w-[240px] truncate">{u.notes || '—'}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openEditUsage(u)} title="Modifica" data-testid={`edit-usage-${u.id}`}>
                                <Pencil className="w-4 h-4 text-gray-500" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setUsageToDelete(u)} title="Elimina" data-testid={`delete-usage-${u.id}`}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Tab Movimenti ───────────────────────── */}
        <TabsContent value="movements" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="text-sm font-semibold text-gray-700">Storico movimenti</div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filtri */}
              <div className="flex flex-wrap gap-2 items-end">
                <div className="min-w-[140px]">
                  <Label className="text-xs">Tipo</Label>
                  <Select value={mFilterType} onValueChange={setMFilterType}>
                    <SelectTrigger data-testid="filter-m-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti</SelectItem>
                      <SelectItem value="purchase">Acquisti</SelectItem>
                      <SelectItem value="usage">Crediti spesi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="min-w-[120px]">
                  <Label className="text-xs">Anno</Label>
                  <Select value={mFilterYear} onValueChange={setMFilterYear}>
                    <SelectTrigger data-testid="filter-m-year"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti</SelectItem>
                      {movementYears.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Dal</Label>
                  <Input type="date" value={mFilterFrom} onChange={(e) => setMFilterFrom(e.target.value)} className="w-[150px]" data-testid="filter-m-from" />
                </div>
                <div>
                  <Label className="text-xs">Al</Label>
                  <Input type="date" value={mFilterTo} onChange={(e) => setMFilterTo(e.target.value)} className="w-[150px]" data-testid="filter-m-to" />
                </div>
                <Button variant="ghost" size="sm" onClick={resetMovementFilters} data-testid="btn-reset-m-filters">
                  <X className="w-4 h-4 mr-1" /> Azzera filtri
                </Button>
              </div>

              {filteredMovements.length === 0 ? (
                <EmptyState label={movements.length === 0 ? "Nessun movimento registrato." : "Nessun movimento corrisponde ai filtri."} />
              ) : (
                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Riferimento</TableHead>
                        <TableHead>Anno competenza</TableHead>
                        <TableHead className="text-right">Importo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredMovements.map(m => (
                        <TableRow key={m.id} data-testid={`movement-row-${m.id}`}>
                          <TableCell>{fmtDate(m.date)}</TableCell>
                          <TableCell>
                            {m.type === 'purchase'
                              ? <Badge className="bg-blue-100 text-blue-700 border-0">Acquisto</Badge>
                              : <Badge className="bg-red-100 text-red-700 border-0">F24</Badge>}
                          </TableCell>
                          <TableCell className="text-sm text-gray-700">{m.reference || '—'}</TableCell>
                          <TableCell><Badge variant="secondary">{m.usable_from_year || '—'}</Badge></TableCell>
                          <TableCell className={`text-right tabular-nums font-semibold ${m.amount >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                            {m.amount >= 0 ? '+' : ''}{fmtEUR(m.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ─── Dialog Acquisto (create/edit) ───────────────────────── */}
      <Dialog open={purchaseDialogOpen} onOpenChange={setPurchaseDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingPurchase ? 'Modifica acquisto' : 'Nuovo acquisto'}</DialogTitle>
            <DialogDescription>Registra un acquisto di credito fiscale.</DialogDescription>
          </DialogHeader>
          <form onSubmit={savePurchase} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Importo nominale *</Label>
                <Input type="number" step="0.01" min="0.01" value={purchaseForm.nominal_amount}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, nominal_amount: e.target.value })}
                  required data-testid="input-nominal-amount" placeholder="100000" />
              </div>
              <div className="space-y-1">
                <Label>Prezzo di acquisto *</Label>
                <Input type="number" step="0.01" min="0.01" value={purchaseForm.purchase_price}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, purchase_price: e.target.value })}
                  required data-testid="input-purchase-price" placeholder="85000" />
              </div>
            </div>
            {formNominal > 0 && formPrice > 0 && (
              <div className="text-xs text-gray-600 bg-gray-50 rounded-md p-2 flex items-center gap-4">
                <span>Sconto: <b className="text-green-700">{fmtEUR(formDiscount)}</b></span>
                <span>% acquisto: <b>{fmtPct(formPct)}</b></span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Data acquisto *</Label>
                <Input type="date" value={purchaseForm.purchase_date}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, purchase_date: e.target.value })}
                  required data-testid="input-purchase-date" />
              </div>
              <div className="space-y-1">
                <Label>Spendibile dal (anno) *</Label>
                <Select value={purchaseForm.usable_from_year} onValueChange={(v) => setPurchaseForm({ ...purchaseForm, usable_from_year: v })}>
                  <SelectTrigger data-testid="input-usable-year"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {YEARS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Riferimento</Label>
              <Input type="text" value={purchaseForm.reference}
                onChange={(e) => setPurchaseForm({ ...purchaseForm, reference: e.target.value })}
                data-testid="input-purchase-reference" placeholder="Es. CF-2026-001" />
            </div>
            <div className="space-y-1">
              <Label>Note</Label>
              <Textarea rows={2} value={purchaseForm.notes}
                onChange={(e) => setPurchaseForm({ ...purchaseForm, notes: e.target.value })}
                data-testid="input-purchase-notes" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPurchaseDialogOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-orange-600 hover:bg-orange-700" data-testid="btn-save-purchase">
                {editingPurchase ? 'Salva modifiche' : 'Registra acquisto'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ─── Dialog Utilizzo (F24) ──────────────────────────────── */}
      <Dialog open={usageDialogOpen} onOpenChange={setUsageDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingUsage ? 'Modifica F24' : 'Registra F24'}</DialogTitle>
            <DialogDescription>Ogni F24 attinge dallo stock dell'anno di competenza selezionato.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveUsage} className="space-y-4">
            <div className="space-y-1">
              <Label>Anno di competenza *</Label>
              <Select
                value={usageForm.usable_from_year}
                onValueChange={(v) => setUsageForm({ ...usageForm, usable_from_year: v })}
              >
                <SelectTrigger data-testid="input-usage-year"><SelectValue placeholder="Seleziona un anno" /></SelectTrigger>
                <SelectContent>
                  {availableUsageYears.map(y => (
                    <SelectItem key={y.year} value={String(y.year)}>
                      Credito {y.year} — Residuo {fmtEURcompact(y.residual_amount)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedYearInfo && (
                <p className="text-xs text-gray-500 mt-1">
                  Residuo disponibile: <b>{fmtEUR(residualForForm)}</b>
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Importo speso *</Label>
                <Input type="number" step="0.01" min="0.01" value={usageForm.amount}
                  onChange={(e) => setUsageForm({ ...usageForm, amount: e.target.value })}
                  required data-testid="input-usage-amount" placeholder="10000" />
              </div>
              <div className="space-y-1">
                <Label>Data utilizzo *</Label>
                <Input type="date" value={usageForm.usage_date}
                  onChange={(e) => setUsageForm({ ...usageForm, usage_date: e.target.value })}
                  required data-testid="input-usage-date" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Riferimento operazione</Label>
              <Input type="text" value={usageForm.reference}
                onChange={(e) => setUsageForm({ ...usageForm, reference: e.target.value })}
                data-testid="input-usage-reference" placeholder="Es. F24-04-2026" />
            </div>
            <div className="space-y-1">
              <Label>Note</Label>
              <Textarea rows={2} value={usageForm.notes}
                onChange={(e) => setUsageForm({ ...usageForm, notes: e.target.value })}
                data-testid="input-usage-notes" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setUsageDialogOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-orange-600 hover:bg-orange-700" data-testid="btn-save-usage">
                {editingUsage ? 'Salva modifiche' : 'Registra F24'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ─── Dialog dettaglio acquisto ──────────────────────────── */}
      <Dialog open={!!detailPurchase} onOpenChange={(open) => { if (!open) setDetailPurchase(null); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Dettaglio acquisto {detailPurchase?.reference ? `— ${detailPurchase.reference}` : ''}</DialogTitle>
          </DialogHeader>
          {detailPurchase && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <InfoRow label="Data acquisto" value={fmtDate(detailPurchase.purchase_date)} />
                <InfoRow label="Anno di competenza" value={detailPurchase.usable_from_year} />
                <InfoRow label="Importo nominale" value={fmtEUR(detailPurchase.nominal_amount)} />
                <InfoRow label="Prezzo pagato" value={fmtEUR(detailPurchase.purchase_price)} />
                <InfoRow label="Sconto ottenuto" value={fmtEUR(detailPurchase.discount_amount)} highlight="green" />
                <InfoRow label="% acquisto" value={fmtPct(detailPurchase.purchase_percentage)} />
              </div>
              {detailPurchase.notes && (
                <div className="bg-gray-50 rounded-md p-3 text-sm text-gray-700 whitespace-pre-wrap">
                  <div className="text-xs text-gray-500 mb-1">Note</div>
                  {detailPurchase.notes}
                </div>
              )}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900">
                <b>Nota:</b> il residuo viene tracciato a livello di <b>anno di competenza {detailPurchase.usable_from_year}</b>,
                non del singolo acquisto. Consulta la card "Credito residuo per anno" nella parte alta per il saldo aggiornato.
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDetailPurchase(null)}>Chiudi</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── Conferma elimina acquisto ─────────────────────────── */}
      <AlertDialog open={!!purchaseToDelete} onOpenChange={(open) => { if (!open) setPurchaseToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l&apos;acquisto?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare l&apos;acquisto di credito fiscale
              {purchaseToDelete?.reference ? <> <b>&quot;{purchaseToDelete.reference}&quot;</b></> : ''} del <b>{fmtDate(purchaseToDelete?.purchase_date)}</b>. Il residuo dell&apos;anno di competenza <b>{purchaseToDelete?.usable_from_year}</b> verrà ricalcolato. Questa operazione è irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeletePurchase} className="bg-red-600 hover:bg-red-700" data-testid="btn-confirm-delete-purchase">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ─── Conferma elimina utilizzo ─────────────────────────── */}
      <AlertDialog open={!!usageToDelete} onOpenChange={(open) => { if (!open) setUsageToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l&apos;utilizzo?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare l&apos;F24 del <b>{fmtDate(usageToDelete?.usage_date)}</b> di <b>{fmtEUR(usageToDelete?.amount)}</b> (anno di competenza <b>{usageToDelete?.usable_from_year}</b>). Il residuo dell&apos;anno verrà ripristinato automaticamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteUsage} className="bg-red-600 hover:bg-red-700" data-testid="btn-confirm-delete-usage">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Sub-componenti ────────────────────────────────────────────────
function SummaryCard({ icon: Icon, label, value, sub, color, testid }) {
  const colorMap = {
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    red: 'bg-red-50 text-red-700',
    amber: 'bg-amber-50 text-amber-700',
  };
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${colorMap[color] || 'bg-gray-50 text-gray-700'}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-gray-500">{label}</div>
            <div className="text-xl font-bold text-gray-900 truncate">{value}</div>
            {sub && <div className="text-[11px] text-gray-500 mt-1">{sub}</div>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function InfoRow({ label, value, highlight }) {
  const colorMap = { green: 'text-green-700', red: 'text-red-600' };
  return (
    <div className="flex justify-between items-baseline border-b pb-1">
      <span className="text-xs text-gray-500">{label}</span>
      <span className={`font-semibold ${highlight ? colorMap[highlight] : 'text-gray-900'} tabular-nums`}>{value}</span>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="border border-dashed rounded-lg p-8 text-center">
      <Coins className="w-8 h-8 text-gray-300 mx-auto mb-2" />
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  );
}
